/* eslint-disable max-lines */
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Organization } from '@utils/types/organization/organization.ts';
import cn from 'classnames';
import Input from '@components/atoms/ui-kit/input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import CountrySelect from '@components/shared/country-code.tsx';
import { Popover, Transition } from '@headlessui/react';
import { BlockPicker } from 'react-color';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useUpdateOrganizationLogo } from '@utils/api/users/use-update-logo.tsx';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { useDeleteOrganizationLogo } from '@utils/api/users/use-delete-organization-logo.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { Text } from '@components/atoms/ui-kit/text';
import { Card } from '@components/atoms/ui-kit/card';
import { BaseError } from '@utils/types/shared/base';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { isBrandColorProblematic } from '@utils/helpers/color-utils';
import { Palette, Trash2, Save, Image, Upload } from 'lucide-react';
import PhoneField from '@components/atoms/ui-kit/phone-input.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import { DocumentTranslationLanguage, Option } from '@utils/types/shared.ts';

type CSSWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

type ApiErrorData = {
  detail?: string | Array<{ loc: string[]; msg: string }>;
  message?: string;
  error_description?: string;
};

type OrganizationDetailsFormProps = {
  control: Control<{
    name: string;
    email: string;
    phone?: string;
    address_street: string;
    address_city?: string;
    address_postal_code?: string;
    address_country_code?: string;
    billing_vat: string;
    diarization?: boolean;
    allow_magic_link_export?: boolean;
    default_translation_languages?: string[];
  }>;
  errors: FieldErrors<Organization>;
  onSubmit: (data: Organization) => void;
  organizationData: Organization | undefined;
  handleOrganzationSubmit: UseFormHandleSubmit<Organization>;
  isOrganizationDirty: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: (...args: any[]) => void;
};

export default function OrganizationDetailsForm({
  control,
  errors,
  onSubmit,
  organizationData,
  handleOrganzationSubmit,
  isOrganizationDirty,
  setValue
}: OrganizationDetailsFormProps) {
  const { t } = useTranslation(); // ← use once at top level
  const { getColour, theme } = useThemeContext();
  const { isReseller } = useRoles();
  const queryClient = useQueryClient();

  const [color, setColor] = useState<string>('');
  const [deleteLogoIsOpen, setDeleteLogoIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  // Queries
  const orgId = Number(organizationData?.id);
  const { mutate: deleteLogo } = useDeleteOrganizationLogo(orgId);
  const { mutate: updateLogo, isError } = useUpdateOrganizationLogo(orgId);

  useEffect(() => {
    if (organizationData?.reseller_branding?.brand_color) {
      // Always use the original brand color from the database
      // Don't modify it - let the UI components handle display adaptation
      setColor(organizationData.reseller_branding.brand_color);
    } else {
      setColor('');
    }
  }, [organizationData]);

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const active = getColour('activeText');
  const text = getColour('normalText');
  const surface = getColour('appSoft');
  const borderBase = getColour('hairline');
  const borderHover = getColour('hairline');
  const placeholderColor = getColour('inactiveText');
  const disabledBg = pick('rgba(17,24,39,0.03)', 'rgba(255,255,255,0.06)');

  const controlBg = theme === 'dark' ? 'transparent' : surface;
  const menuBg = surface;

  const brandStyleVars: CSSWithVars = {
    '--in-bg': surface,
    '--in-fg': text,
    '--in-ph': placeholderColor,
    '--in-border': borderBase,
    '--in-border-hover': borderHover,
    '--in-ring': active,
    '--in-disabled-bg': disabledBg,
    '--in-ctrl-bg': controlBg,
    '--in-menu-bg': menuBg
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setLogoFile(file);
      setValue('logo', file as File);

      updateLogo(file, {
        onSuccess: () => {
          toast.success(t('logo-updated-successfully'));
          void queryClient.invalidateQueries(['organization']);
        },
        onError: (error: AxiosError<BaseError>) => {
          const fallbackMessage = t('failed-to-update-the-logo');
          const data = error?.response?.data as ApiErrorData | undefined;

          let messages: string[] = [];

          if (Array.isArray(data?.detail)) {
            messages = data.detail.map(err => {
              const field = err.loc?.[1] ?? 'field';
              return `${field}: ${err.msg}`;
            });
          } else if (data?.message) {
            messages = [data.message];
          } else if (data?.error_description) {
            messages = [data.error_description];
          } else {
            messages = [fallbackMessage];
          }

          toast.error(messages.join('\n'));
        }
      });
    }
  };

  const handleDeleteLogo = async () => {
    setIsDeleting(true);

    deleteLogo(undefined, {
      onSuccess: () => {
        toast.success(t('logo-deleted-successfully'));
        setDeleteLogoIsOpen(false);
        setIsDeleting(false);
        setLogoFile(null);
        setValue('reseller_branding.brand_logo', '', { shouldDirty: true });
        void queryClient.invalidateQueries(['organization']);
      },
      onError: (error: AxiosError<BaseError>) => {
        const fallbackMessage = t('failed-to-delete-the-logo');
        const data = error?.response?.data as ApiErrorData | undefined;

        let messages: string[] = [];

        if (Array.isArray(data?.detail)) {
          messages = data.detail.map(err => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (data?.message) {
          messages = [data.message];
        } else if (data?.error_description) {
          messages = [data.error_description];
        } else {
          messages = [fallbackMessage];
        }

        toast.error(messages.join('\n'));
        setIsDeleting(false);
      }
    });
  };

  return (
    <Card padding="sm" className="sm:p-6">
      <form onSubmit={handleOrganzationSubmit(onSubmit)}>
        <PageHeader
          title={t('organization-details')}
          caption={t('update-organization-description')}
          className="border-b-0 mb-4"
          contentClassName="px-0 py-0"
        >
          <Button
            icon={<Save width={16} />}
            type="submit"
            label={t('update')}
            variant="secondary"
            size="small"
            disabled={!isOrganizationDirty}
          />
        </PageHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:gap-6">
          <Controller
            name="name"
            control={control}
            defaultValue={organizationData?.name || ''}
            render={({ field }) => (
              <Input
                {...field}
                label={t('name')}
                id="name"
                name="name"
                autoComplete="name"
                error={errors.name?.message}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            defaultValue={organizationData?.email || ''}
            render={({ field }) => (
              <Input
                {...field}
                label={t('email')}
                id="email"
                type="email"
                name="email"
                autoComplete="email"
                error={errors.email?.message}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            defaultValue={organizationData?.phone || ''}
            render={({ field }) => (
              <PhoneField
                id="phone"
                label={t('phone')}
                name={field.name}
                value={field.value || ''}
                onChange={(e164: string) => field.onChange(e164)}
                defaultCountry={(
                  organizationData?.address_country_code || 'NL'
                ).toUpperCase()}
                placeholder="612345678"
                className="h-full"
                messages={{
                  required: t('field-required'),
                  invalid: t('enter-a-valid-international-phone-number')
                }}
                error={errors.phone?.message}
              />
            )}
          />

          <Controller
            name="address_street"
            control={control}
            defaultValue={organizationData?.address_street || ''}
            render={({ field }) => (
              <Input
                {...field}
                label={t('street')}
                id="address_street"
                name="address_street"
                autoComplete="address_street"
                error={errors.address_street?.message}
              />
            )}
          />

          <Controller
            name="address_city"
            control={control}
            defaultValue={organizationData?.address_city || ''}
            render={({ field }) => (
              <Input
                {...field}
                label={t('city')}
                id="address_city"
                name="address_city"
                autoComplete="address_city"
                error={errors.address_city?.message}
              />
            )}
          />

          <Controller
            name="address_postal_code"
            control={control}
            defaultValue={organizationData?.address_postal_code || ''}
            render={({ field }) => (
              <Input
                {...field}
                label={t('postal-code')}
                id="address_postal_code"
                name="address_postal_code"
                autoComplete="address_postal_code"
                error={errors.address_postal_code?.message}
              />
            )}
          />

          <Controller
            name="address_country_code"
            control={control}
            defaultValue={
              organizationData?.address_country_code?.toLowerCase() || ''
            }
            render={({ field }) => (
              <CountrySelect
                {...field}
                label={t('country')}
                defaultValue={organizationData?.address_country_code || 'nl'}
                onChange={(selectedOption: { value: string } | null) => {
                  field.onChange(selectedOption?.value.toLowerCase() || '');
                }}
                error={errors.address_country_code?.message}
              />
            )}
          />

          <Controller
            name="billing_vat"
            control={control}
            defaultValue={organizationData?.billing_vat || ''}
            render={({ field }) => (
              <Input
                {...field}
                label={t('billing-vat')}
                id="billing_vat"
                name="billing_vat"
                autoComplete="billing_vat"
                error={errors.billing_vat?.message}
              />
            )}
          />

          {isReseller && (
            <div className="sm:col-span-2" style={brandStyleVars}>
              <Text
                as="label"
                htmlFor="brand_color"
                size="sm"
                weight="semibold"
              >
                {t('brand')}
              </Text>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                {/* Color picker */}
                <div
                  className="rounded-2xl border p-3"
                  style={{
                    borderColor: getColour('hairline'),
                    backgroundColor: getColour('thirdLayer')
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-2xl shadow-inner flex items-center justify-center border shrink-0"
                      style={{
                        backgroundColor: color || getColour('appSoft'),
                        borderColor: color
                          ? 'transparent'
                          : getColour('hairline')
                      }}
                    >
                      {!color && (
                        <Palette
                          size={14}
                          style={{ color: getColour('inactiveText') }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Text as="p" size="xs" weight="medium" truncate>
                        {color || t('no-color-selected')}
                      </Text>
                      {color && isBrandColorProblematic(color, theme) && (
                        <p
                          className="text-xs mt-0.5"
                          style={{
                            color:
                              theme === 'dark'
                                ? 'rgb(251,191,36)'
                                : 'rgb(217,119,6)'
                          }}
                        >
                          {t('color-auto-adjusted') ||
                            'Auto-adjusted for readability'}
                        </p>
                      )}
                    </div>
                  </div>

                  <div style={brandStyleVars}>
                    <Popover className="relative">
                      <Popover.Button
                        type="button"
                        id="brand_color"
                        aria-label={t('select-color') || 'Select color'}
                      >
                        <Button
                          label={color ? t('change-color') : t('select-color')}
                          icon={<Palette className="h-3.5 w-3.5" />}
                          variant="secondary"
                          size="xs"
                        />
                      </Popover.Button>

                      <Transition
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Popover.Panel
                          className={cn(
                            'absolute z-50 bottom-full mb-2 rounded-xl p-4 shadow-xl ring-1',
                            'bg-[var(--in-menu-bg)] text-[var(--in-fg)]',
                            'border border-[var(--in-border)] ring-[var(--in-border)]'
                          )}
                        >
                          <div className="mb-3 flex items-center justify-between gap-6">
                            <Text as="span" size="sm" weight="medium">
                              {t('pick-a-color')}
                            </Text>
                            <button
                              type="button"
                              className="text-xs hover:underline hover:opacity-80"
                              style={{
                                color:
                                  theme === 'dark'
                                    ? 'rgb(248,113,113)'
                                    : 'rgb(220,38,38)'
                              }}
                              onClick={() => {
                                setColor('');
                                setValue('reseller_branding.brand_color', '', {
                                  shouldDirty: true
                                });
                              }}
                            >
                              {t('clear')}
                            </button>
                          </div>

                          <div className="rounded-2xl overflow-hidden bg-app-soft">
                            <BlockPicker
                              color={color || getColour('activeText')}
                              triangle="hide"
                              width="220"
                              onChangeComplete={c => {
                                setColor(c.hex);
                                setValue(
                                  'reseller_branding.brand_color',
                                  c.hex,
                                  { shouldDirty: true }
                                );
                              }}
                            />
                          </div>

                          {errors?.reseller_branding?.brand_color && (
                            <Text
                              as="p"
                              size="xs"
                              tone="danger"
                              className="mt-2"
                            >
                              {errors.reseller_branding?.brand_color?.message}
                            </Text>
                          )}
                        </Popover.Panel>
                      </Transition>
                    </Popover>
                  </div>
                </div>

                {/* Logo upload */}
                <div
                  className="rounded-2xl border p-3"
                  style={{
                    borderColor: getColour('hairline'),
                    backgroundColor: getColour('thirdLayer')
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-2xl flex items-center justify-center border shrink-0 overflow-hidden"
                      style={{
                        borderColor: getColour('hairline'),
                        backgroundColor: getColour('appSoft')
                      }}
                    >
                      {logoFile ||
                      organizationData?.reseller_branding?.brand_logo ? (
                        <img
                          src={
                            logoFile
                              ? URL.createObjectURL(logoFile)
                              : organizationData?.reseller_branding
                                  ?.brand_logo || ''
                          }
                          alt="Logo"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <Image
                          size={14}
                          style={{ color: getColour('inactiveText') }}
                        />
                      )}
                    </div>
                    <Text
                      as="p"
                      size="xs"
                      weight="medium"
                      truncate
                      className="flex-1 min-w-0"
                    >
                      {logoFile
                        ? logoFile.name
                        : organizationData?.reseller_branding?.brand_logo
                          ? t('logo-uploaded')
                          : t('no-logo-selected')}
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="logo">
                      <Button
                        label={
                          logoFile ||
                          organizationData?.reseller_branding?.brand_logo
                            ? t('replace-logo')
                            : t('upload-logo')
                        }
                        icon={<Upload className="h-3.5 w-3.5" />}
                        variant="secondary"
                        size="xs"
                        onClick={() => document.getElementById('logo')?.click()}
                      />
                      <input
                        id="logo"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="sr-only"
                      />
                    </label>
                    {organizationData?.reseller_branding?.brand_logo && (
                      <Button
                        label={t('remove')}
                        icon={<Trash2 className="h-3.5 w-3.5" />}
                        variant="smoke"
                        size="xs"
                        onClick={() => setDeleteLogoIsOpen(true)}
                      />
                    )}
                  </div>

                  {errors?.reseller_branding?.brand_logo && (
                    <Text as="p" size="xs" tone="danger" className="mt-1">
                      {errors.reseller_branding?.brand_logo?.message}
                    </Text>
                  )}
                  {isError && (
                    <Text as="p" size="xs" tone="danger" className="mt-1">
                      {t('logo-update-error') ||
                        'An error occurred while updating the logo.'}
                    </Text>
                  )}
                </div>
              </div>
            </div>
          )}
          <div></div>
          <div className="rounded-2xl border border-hairline px-3 py-2.5 flex items-center">
            <Controller
              name="diarization"
              control={control}
              defaultValue={organizationData?.diarization || false}
              render={({ field }) => (
                <Checkbox
                  id="diarization"
                  checked={!!field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  label={t('separate-speakers')}
                  description={
                    t('diarization-description') ||
                    'Identify and label different speakers in the transcript'
                  }
                  variant="primary"
                  error={errors.diarization?.message as string | undefined}
                />
              )}
            />
          </div>

          <div className="rounded-2xl border border-hairline px-3 py-2.5 flex items-center">
            <Controller
              name="allow_magic_link_export"
              control={control}
              defaultValue={organizationData?.allow_magic_link_export ?? true}
              render={({ field }) => (
                <Checkbox
                  id="allow_magic_link_export"
                  checked={!!field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  label={t('allow-magic-link-export')}
                  description={t('allow-magic-link-export-description')}
                  variant="primary"
                  error={
                    errors.allow_magic_link_export?.message as
                      | string
                      | undefined
                  }
                />
              )}
            />
          </div>

          <div className="sm:col-span-2">
            <Controller
              name="default_translation_languages"
              control={control}
              defaultValue={
                organizationData?.default_translation_languages || []
              }
              render={({ field }) => {
                const options: Option[] = Object.keys(
                  DocumentTranslationLanguage
                ).map(
                  key =>
                    ({
                      label: key,
                      value:
                        DocumentTranslationLanguage[
                          key as keyof typeof DocumentTranslationLanguage
                        ]
                    }) as Option
                );
                const selected: Option[] = ((field.value as string[]) || [])
                  .map(code => {
                    const name = Object.entries(
                      DocumentTranslationLanguage
                    ).find(([, v]) => v === code.toUpperCase())?.[0];
                    return name
                      ? ({
                          label: name,
                          value: code.toUpperCase()
                        } as Option)
                      : null;
                  })
                  .filter(Boolean) as Option[];
                return (
                  <SelectInput
                    id="default-translation-languages"
                    label={t('default-translation-languages')}
                    options={options}
                    value={selected}
                    onChange={(opts: Option[]) =>
                      field.onChange((opts ?? []).map(o => o.value))
                    }
                    placeholder={t('select-translation-languages')}
                    isMulti
                    menuPlacement="top"
                    size="small"
                  />
                );
              }}
            />
            <Text as="p" size="xs" tone="muted" className="mt-2">
              {t('default-translation-languages-description')}
            </Text>
          </div>
        </div>
      </form>

      {deleteLogoIsOpen && (
        <ConfirmationModal
          isOpen={deleteLogoIsOpen}
          onClose={() => setDeleteLogoIsOpen(false)}
          onConfirm={handleDeleteLogo}
          size="small"
          title={t('delete-logo-confirmation')}
          caption={t('are-you-sure-you-want-to-delete-this-logo')}
          confirmLabel={isDeleting ? t('deleting') : t('delete')}
          cancelLabel={t('cancel')}
          confirmDisabled={isDeleting}
          destructive
          autoFocusConfirm
        />
      )}
    </Card>
  );
}
